<template>
  <v-layout row wrap>
    <!-- Rules List -->
    <v-flex :xs10="!addRulesSheet" :xs5="addRulesSheet">
      <v-data-table :headers="ruleHeaders" :items="ruleSetRulesList" ref="draggableTable" :pagination.sync="pagination"
        :rows-per-page-items="rowsPerPagesArray">
        <template slot="items" slot-scope="props">
          <tr>
            <td class="handle" style="max-width: 28px" v-if="displayDragHandle">
              <v-icon>drag_handle</v-icon>
            </td>
            <td class="text-xs-center" v-if="displayDragHandle">{{ props.item.sequence + 1 }}</td>
            <td class="text-xs-left">{{ props.item.title }}</td>
            <td class="text-xs-left">{{ getProviderName(props.item.secondaryOwner) }}</td>
            <td class="text-xs-left" v-if="!addRulesSheet">{{ props.item._enabled }}
            </td>
            <td class="text-xs-left" v-if="!addRulesSheet">{{ props.item._added }}</td>
            <td class="text-xs-left" v-if="!addRulesSheet">{{ props.item._updated }}</td>
            <td @click.stop="stopProp" class="justify-center layout px-0">
              <!-- Delete icon -->
              <v-tooltip bottom slot="activator">
                <template v-slot:activator="{ on }">
                  <v-icon small v-on="on" @click="showDeleteRuleDialog(props.item)"
                    class="error--text text--lighten-1 mr-1">
                    delete
                  </v-icon>
                </template>
                <span>Delete Rule</span>
              </v-tooltip>
              <!-- More menu -->
              <v-menu bottom left>
                <v-tooltip bottom slot="activator">
                  <template v-slot:activator="{ on }">
                    <v-btn icon medium v-on="on" class="grey--text text--darken-2">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <span>More</span>
                </v-tooltip>
                <v-list>
                  <v-list-tile @click="showRuleQuickView(props.item)">
                    <v-list-tile-title>{{ messages.label.VIEW_DETAILS }}</v-list-tile-title>
                    <v-icon small right>
                      open_in_new
                    </v-icon>
                  </v-list-tile>
                  <v-list-tile>
                    <v-list-tile-title>{{ messages.label.TEST }}</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile @click="goToEditView(props.item)">
                    <v-list-tile-title>{{ messages.label.EDIT }}</v-list-tile-title>
                    <v-icon small right>exit_to_app</v-icon>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
        <!-- No Data -->
        <template slot="no-data">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            {{ messages.info.NO_RULES_IN_RULESET }}
          </v-alert>
        </template>
      </v-data-table>
    </v-flex>
    <v-flex xs7 v-show="addRulesSheet">
      <AddRules />
    </v-flex>
    <v-flex xs2 v-show="!addRulesSheet">
      <v-btn color="primary" flat @click="showAddRulesSheet()">
        <v-icon left>add_circle</v-icon><span>{{ messages.label.ADD_RULES }}</span>
      </v-btn>
    </v-flex>
    <!-- Dialogs -->
    <v-dialog v-model="ruleQuickView" max-width="1200">
      <RuleQuickView />
    </v-dialog>
    <!-- Delete dialog -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card>
        <v-card-title class="subheading ttu">
          {{ messages.label.DELETE_RULE }}
        </v-card-title>
        <v-card-text>
          <p>{{ messages.info.DELETE_RULE_MSG }} <b>{{ ruleToDelete.title }}</b> ? </p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey lighten-1 white--text" @click="deleteDialog = false">
            {{ messages.label.CANCEL }}
          </v-btn>
          <v-btn color="error" @click="deleteRule(ruleToDelete)">
            {{ messages.label.DELETE }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>
<script>
import { mapState, mapMutations } from "vuex"
import messages from "@/MetadataRules/messages.json"
import RuleQuickView from "@/MetadataRules/Rule/RuleQuickView"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import AddRules from "@/MetadataRules/RuleSet/AddRules"
import { SET_RULESET_RULES } from "@/store/constants/mutation-types"
import { metadataRules } from "@/store/metadataRules"
import Sortable from "sortablejs"

export default {
  name: "RuleSetRules",
  components: {
    RuleQuickView,
    AddRules
  },
  data() {
    return {
      selectedRule: {},
      addRulesSheet: false,
      ruleQuickView: false,
      rowsPerPagesArray: [10, 25],
      deleteDialog: false,
      ruleToDelete: {},
      pagination: {
        sortBy: "title",
        page: 1,
        descending: false
      }
    }
  },
  computed: {
    ...mapState("metadataRules", {
      addRulesFilters: state => state.addRulesFilters
    }),
    ruleHeaders() {
      return this.getRuleHeaders()
    },
    ruleSetRulesList: {
      get() {
        return metadataRules.state.ruleSetRulesList
      },
      set(newValue) {
        metadataRules.state.ruleSetRulesList = newValue
      }
    },
    displayDragHandle() {
      let display = false
      if (this.addRulesFilters.ruleSetType === "TRANSFORMATION") {
        display = true
      }
      return display
    }
  },
  methods: {
    ...mapMutations("metadataRules", [
      SET_RULESET_RULES
    ]),
    /**
     * getRuleHeaders - rules data table headers
     * @returns {array}
     */
    getRuleHeaders() {
      let headersToDisplay = []
      if (this.displayDragHandle) {
        headersToDisplay = [{
          text: "",
          align: "left",
          sortable: false
        },
        {
          text: "SEQUENCE",
          align: "center",
          value: "sequence",
          sortable: true
        }]
      }
      let commonHeaders = [{
        text: messages.label.TH_TITLE,
        align: "left",
        value: "title",
        sortable: true
      },
      {
        text: messages.label.TH_PROVIDER,
        sortable: false,
        align: "left",
        value: "secondaryOwner"
      }
      ]
      headersToDisplay = headersToDisplay.concat(commonHeaders)
      if (this.addRulesSheet) {
        headersToDisplay.push({ text: "", sortable: false })
        return headersToDisplay
      } else {
        headersToDisplay.push({
          text: messages.label.TH_ENABLED,
          sortable: true,
          align: "left",
          value: "_enabled"
        })
        headersToDisplay.push({
          text: messages.label.TH_ADDED,
          value: "_added",
          sortable: true,
          align: "left"
        })
        headersToDisplay.push({
          text: messages.label.TH_UPDATED,
          value: "_updated",
          sortable: true,
          align: "left"
        })
        headersToDisplay.push({ text: "", sortable: false })
        return headersToDisplay
      }
    },

    /**
     * showAddRulesSheet - Show condition details
     * @param {object} rule
     */
    showAddRulesSheet() {
      this.addRulesSheet = true
      MetadataRulesService.$emit("event:showAddRulesSheet")
    },

    /**
     * hideAddRulesSheet - Hide condition details view
     */
    hideAddRulesSheet() {
      this.addRulesSheet = false
    },

    /**
     * resetData - Reset data
     */
    resetData() {
      this.selectedRule = {}
    },

    /**
     * loadRules - updated view with rules list
     */
    loadRules() {
      this.resetData()
    },

    /**
     * stopProp - Do nothing, just to stop event propogation
     */
    stopProp() { },

    /**
     * showDeleteRuleDialog - Show dialog to delete condition
     * @param {object} rule
     */
    showDeleteRuleDialog(rule) {
      this.deleteDialog = true
      this.ruleToDelete = rule
    },
    /**
     * Reset / update sequence numbers
     */
    resetRulesSequenceNumbers() {
      if (this.ruleSetRulesList.length > 0) {
        this.$lodash.forEach(this.ruleSetRulesList, (rule, index) => {
          rule["sequence"] = index
        })
      }
    },

    /**
     * Sort rules within rule-set based on sequence number
     */
    sortRulesOnSequence() {
      if (this.ruleSetRulesList.length > 0) {
        let sortedList = this.$lodash.sortBy(this.ruleSetRulesList, "sequence")
        this.ruleSetRulesList = []
        this.$nextTick(() => {
          this.ruleSetRulesList = sortedList
        })
      }
    },

    /**
     * deleteCondition - Delete existing rule
     * @param {object} condition
     */
    deleteRule(rule) {
      let ruleIndex = this.$lodash.findIndex(this.ruleSetRulesList, (ruleObj) => {
        return rule.id === ruleObj.id
      })
      if (ruleIndex !== -1) {
        this.ruleSetRulesList.splice(ruleIndex, 1)
        this.SET_RULESET_RULES(this.ruleSetRulesList)
        this.deleteDialog = false
        this.hideAddRulesSheet()
      }
      this.resetRulesSequenceNumbers()
      this.sortRulesOnSequence()
    },

    /**
     * showRuleQuickView - Show rule quick view in dialog
     */
    showRuleQuickView(rule) {
      this.ruleQuickView = true
      MetadataRulesService.$emit("event:showRuleQuickViewDialog", rule)
    },

    /**
     * hideRuleQuickView - Hide quick view dialog
     */
    hideRuleQuickView() {
      this.ruleQuickView = false
    },

    /**
     * goToEditView - Navigate to Edit view
     */
    goToEditView(rule) {
      let id = rule.id.split("/").pop()
      let ownerId = rule.ownerId
      let location = "/#/metadata-rules/rule/" + id + "?account=" + ownerId
      window.open(location, "_blank")
    },

    /**
     * validateRuleSetRules -
     */
    validateRuleSetRules() {
      // NOTE: For now passing 'true' but might have to come back and see if we need to do
      // something else here
      MetadataRulesService.$emit("event:ruleSetRulesValidation", true)
    },

    /**
     * getOwnerName
     * @param {stirng} secondaryOwner
     */
    getProviderName(secondaryOwner) {
      return MetadataRulesService.getProviderName(secondaryOwner)
    },
    /**
     * setPagination sorting param based on changed ruleSetType
     */
    setPagination(ruleSetType) {
      if (ruleSetType === "TRANSFORMATION") {
        this.pagination.sortBy = "sequence"
      } else if (ruleSetType === "VALIDATION") {
        this.pagination.sortBy = "title"
      }
    }
  },
  created() {
    /**
    * Non reactive properties
    */
    this.messages = messages

    /**
     * Events
     */
    MetadataRulesService.$on("event:ruleSetDataRetrieved", this.loadRules)
    MetadataRulesService.$on("event:validateRuleSetRules", this.validateRuleSetRules)
    MetadataRulesService.$on("event:hideAddRulesSheet", this.hideAddRulesSheet)
    MetadataRulesService.$on("event:hideRuleQuickViewDialog", this.hideRuleQuickView)
    MetadataRulesService.$on("event:ruleSetTypeChanged", this.setPagination)
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:ruleSetDataRetrieved", this.loadRules)
    MetadataRulesService.$off("event:validateRuleSetRules", this.validateRuleSetRules)
    MetadataRulesService.$off("event:hideAddRulesSheet", this.hideAddRulesSheet)
    MetadataRulesService.$off("event:hideRuleQuickViewDialog", this.hideRuleQuickView)
    MetadataRulesService.$off("event:ruleSetTypeChanged", this.setPagination)
  },
  mounted() {
    let table = this.$refs.draggableTable.$el.getElementsByTagName("tbody")[0]
    const _self = this
    Sortable.create(table, {
      handle: ".handle", // Use handle so user can select text
      onEnd({ newIndex, oldIndex }) {
        const rowSelected = _self.ruleSetRulesList.splice(oldIndex, 1)[0] // Get the selected row and remove it
        _self.ruleSetRulesList.splice(newIndex, 0, rowSelected) // Move it to the new index
        _self.resetRulesSequenceNumbers()
        _self.sortRulesOnSequence()
      }
    })
  }
}
</script>
<style>
.handle {
  cursor: move !important;
  cursor: -webkit-grabbing !important;
}
</style>



// WEBPACK FOOTER //
// src/MetadataRules/RuleSet/RuleSetRules.vue